///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Storage Manager tab for IndexedDB databases: inspect stores, clear data,
/// vacuum orphaned space, and delete databases.

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ChevronDown, ChevronRight, Lock, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import type { StorageTabProps } from '@/components/features/dev-tools/storage-manager/StorageManager'
import { formatBytes, type IndexedDBInfo } from '@/components/features/dev-tools/storage-manager/storageScanner'
import {
	clearObjectStore,
	deleteIndexedDBDatabase,
	vacuumIndexedDBDatabase,
} from '@/components/features/dev-tools/storage-manager/storageActions'

/// The handles database stores File System Access API permissions to local
/// files, which the user cannot re-grant without re-picking files. Protect it
/// from casual deletion.
function isProtectedDb(db: IndexedDBInfo): boolean {
	return db.name === 'EarthBrowserHandles' || db.objectStores.some(s => s.name === 'handles')
}

function DatabaseCard({ db, hasOrphanedStorage, busy, runAction }: {
	db: IndexedDBInfo
	hasOrphanedStorage: boolean
} & Pick<StorageTabProps, 'busy' | 'runAction'>) {
	const [expanded, setExpanded] = useState(false)

	const totalRecords = db.objectStores.reduce((sum, s) => sum + s.recordCount, 0)
	const isProtected = isProtectedDb(db)
	const isCacheDb = db.name === 'EarthBrowserCache'

	const onVacuum = () => {
		if (!confirm(`Vacuum "${db.name}"?\n\nThis exports all data, deletes the database, recreates it, and reloads the page to reclaim orphaned storage space.`)) {
			return
		}
		void runAction(async () => {
			await vacuumIndexedDBDatabase(db)
			alert('Database vacuumed successfully. Reloading page…')
			window.location.reload()
		})
	}

	const onDelete = () => {
		if (!confirm(`Delete database "${db.name}"? This cannot be undone.`)) {
			return
		}
		void runAction(() => deleteIndexedDBDatabase(db.name))
	}

	const onClearStore = (storeName: string) => {
		if (!confirm(`Clear all records from "${storeName}" in "${db.name}"? The store structure is preserved. This cannot be undone.`)) {
			return
		}
		void runAction(() => clearObjectStore(db.name, storeName))
	}

	return (
		<Card>
			<CardContent className="pt-4 space-y-2">
				<div className="flex items-center justify-between gap-2">
					<div className="flex items-center gap-2 min-w-0">
						<button onClick={() => setExpanded(prev => !prev)} className="hover:bg-muted/50 rounded p-1 shrink-0">
							{expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
						</button>
						<div className="min-w-0">
							<div className="text-sm font-medium flex items-center gap-2">
								<span className="truncate">{db.name}</span>
								{isProtected && (
									<Badge variant="outline" className="text-xs shrink-0">
										<Lock className="h-3 w-3 mr-1" />
										Protected
									</Badge>
								)}
								{isCacheDb && (
									<Badge variant="outline" className="text-xs shrink-0">Cache — safe to clear</Badge>
								)}
								{hasOrphanedStorage && (
									<Badge variant="destructive" className="text-xs shrink-0">Orphaned data</Badge>
								)}
							</div>
							<div className="text-xs text-muted-foreground">
								Version {db.version} • {db.objectStores.length} stores • {totalRecords.toLocaleString()} records
							</div>
							{isProtected && (
								<div className="text-xs text-blue-400">
									Holds File System Access permissions to local files — deletion requires re-picking files
								</div>
							)}
						</div>
					</div>
					<div className="flex items-center gap-2 shrink-0">
						{hasOrphanedStorage && !isProtected && (
							<Button variant="outline" size="sm" onClick={onVacuum} disabled={busy} title="Rebuild to reclaim orphaned storage">
								<RefreshCw className="h-3 w-3 mr-1" />
								Vacuum
							</Button>
						)}
						{!isProtected && (
							<Button variant="destructive" size="sm" onClick={onDelete} disabled={busy} title="Delete database">
								<Trash2 className="h-3 w-3" />
							</Button>
						)}
					</div>
				</div>

				{expanded && (
					<div className="ml-7 space-y-2">
						{db.objectStores.map(store => {
							const storeProtected = store.name === 'handles'
							return (
								<div key={store.name} className="p-3 bg-secondary/50 rounded text-xs space-y-2">
									<div className="flex items-start justify-between gap-2">
										<div className="flex-1 min-w-0">
											<div className="font-medium text-sm">{store.name}</div>
											<div className="text-muted-foreground mt-1">
												{store.recordCount.toLocaleString()} records
												{store.keyPath && ` • Key: ${Array.isArray(store.keyPath) ? store.keyPath.join(', ') : store.keyPath}`}
												{store.autoIncrement && ' • Auto-increment'}
												{store.indexCount > 0 && ` • ${store.indexCount} indexes`}
											</div>
										</div>
										{!storeProtected && (
											<Button
												variant="ghost"
												size="sm"
												onClick={() => onClearStore(store.name)}
												disabled={busy}
												title={`Clear all records from "${store.name}"`}
											>
												<Trash2 className="h-3 w-3" />
											</Button>
										)}
									</div>
									{store.sampleKeys.length > 0 && (
										<div className="pl-3 border-l-2 border-slate-700 space-y-1">
											<div className="text-muted-foreground font-medium">Sample keys:</div>
											{store.sampleKeys.map((key, idx) => (
												<div key={idx} className="font-mono bg-background/50 p-1 rounded break-all">
													{typeof key === 'object' ? JSON.stringify(key) : String(key)}
												</div>
											))}
										</div>
									)}
								</div>
							)
						})}
					</div>
				)}
			</CardContent>
		</Card>
	)
}

export function IndexedDBTab({ snapshot, busy, runAction }: StorageTabProps) {
	// Flag databases that report zero records while IndexedDB storage is large,
	// which usually indicates orphaned data reclaimable via vacuum
	const orphanThreshold = 100 * 1024 * 1024

	return (
		<div className="space-y-3">
			<div className="text-xs text-muted-foreground">
				Estimated total: {formatBytes(snapshot.indexedDBEstimate)} (browser APIs don&apos;t expose per-database sizes)
			</div>

			{snapshot.indexedDBs.map(db => {
				const totalRecords = db.objectStores.reduce((sum, s) => sum + s.recordCount, 0)
				const hasOrphanedStorage = totalRecords === 0 && snapshot.indexedDBEstimate > orphanThreshold
				return (
					<DatabaseCard
						key={db.name}
						db={db}
						hasOrphanedStorage={hasOrphanedStorage}
						busy={busy}
						runAction={runAction}
					/>
				)
			})}

			{snapshot.indexedDBs.length === 0 && (
				<Card>
					<CardContent className="py-6 text-center text-sm text-muted-foreground">
						No IndexedDB databases found
					</CardContent>
				</Card>
			)}
		</div>
	)
}
